import {
  CheckboxBlockField,
  InformationBox,
  LabeledSection,
  SubmitButton,
  TextArea,
  TextSelect,
} from 'components';
import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Participation,
  ParticipationMutationOptions,
  ParticipationReportReason,
  ParticipationUpdateData,
  useCreateEvaluationParticipation,
  useIsAdmin,
  useReportParticipant,
} from '@app/api';
import { Form, Formik } from 'formik';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import * as Yup from 'yup';
import { MutationOptions } from 'react-query';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    marginTop: theme.marginBase * 2,
    marginBottom: theme.marginBase * 2,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
  },
  textAreaContainer: {
    marginTop: 0,
    marginBottom: 0,
  },
  checkContainer: {
    display: 'flex',
    flex: 1,
    gap: theme.marginBase * 2,
    marginBottom: theme.marginBase,
  },
  submitButton: {
    width: 'fit-content',
  },
  infoBox: {
    whiteSpace: 'pre-wrap',
  },
}));

interface Props {
  participation: Participation;
  mutationOptions?: ParticipationMutationOptions;
}

const participationReportTranslationBaseKey =
  'sessions.details.monitoring.meetingRating.participationReport.participationReportReason';

interface Values {
  score: number;
  message: string | undefined;
  reportReason: ParticipationReportReason | undefined;
  shouldReplaceParticipant: boolean | undefined;
}

const initialValues: Values = {
  score: -100,
  message: undefined,
  reportReason: undefined,
  shouldReplaceParticipant: undefined,
};

const validationSchema = Yup.object().shape({
  reportReason: Yup.string()
    .required()
    .oneOf(Object.values(ParticipationReportReason)),
  shouldReplaceParticipant: Yup.boolean().required(),
});

export const MeetingReportReasonSelect = ({
  participation,
  mutationOptions,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { mutate: reportParticipant } = useReportParticipant(mutationOptions);
  const { mutateAsync: createEvaluationParticipation } =
    useCreateEvaluationParticipation(participation.id);

  const ParticipationReportReasonOptions: Array<{
    value: ParticipationReportReason;
    label: string;
  }> = useMemo(
    () => [
      {
        value: ParticipationReportReason.NO_SHOW,
        label: t(
          `${participationReportTranslationBaseKey}.${ParticipationReportReason.NO_SHOW}`,
        ),
      },
      {
        value: ParticipationReportReason.EMERGENCY_WITH_NOTICE,
        label: t(
          `${participationReportTranslationBaseKey}.${ParticipationReportReason.EMERGENCY_WITH_NOTICE}`,
        ),
      },
      {
        value: ParticipationReportReason.PROFILE_LIE,
        label: t(
          `${participationReportTranslationBaseKey}.${ParticipationReportReason.PROFILE_LIE}`,
        ),
      },
      {
        value: ParticipationReportReason.CONNECTION_ISSUES,
        label: t(
          `${participationReportTranslationBaseKey}.${ParticipationReportReason.CONNECTION_ISSUES}`,
        ),
      },
      {
        value: ParticipationReportReason.TECHNICAL_REQUIREMENTS_ISSUES,
        label: t(
          `${participationReportTranslationBaseKey}.${ParticipationReportReason.TECHNICAL_REQUIREMENTS_ISSUES}`,
        ),
      },
      {
        value: ParticipationReportReason.BAD_SURROUNDINGS,
        label: t(
          `${participationReportTranslationBaseKey}.${ParticipationReportReason.BAD_SURROUNDINGS}`,
        ),
      },
    ],
    [t],
  );

  const scoreFromReportReason = (reportReason: ParticipationReportReason) => {
    return [
      ParticipationReportReason.NO_SHOW,
      ParticipationReportReason.PROFILE_LIE,
    ].includes(reportReason)
      ? -100
      : -50;
  };

  const isAdmin = useIsAdmin();

  const submit = async (values: Values) => {
    if (!values.reportReason) {
      return;
    }
    const score = scoreFromReportReason(values.reportReason);
    if (!isAdmin) {
      await createEvaluationParticipation({
        score,
        message: `${values.reportReason}: ${values.message}`,
      });
    }
    reportParticipant({
      participation,
      reportReason: values.reportReason,
      shouldReplaceParticipant: values.shouldReplaceParticipant,
    });
  };

  return (
    <div>
      <Formik
        initialValues={initialValues}
        onSubmit={submit}
        validationSchema={validationSchema}
      >
        {({ values }) => (
          <Form className={classes.container}>
            <TextSelect
              noMargin
              name="reportReason"
              options={ParticipationReportReasonOptions}
              label={t(
                'sessions.details.monitoring.meetingRating.participationReport.label',
              )}
              placeholder={t(
                'sessions.details.monitoring.meetingRating.participationReport.placeholder',
              )}
            />
            {values.reportReason &&
              !isAdmin &&
              values.reportReason !== ParticipationReportReason.NO_SHOW && (
                <TextArea
                  noMargin
                  name="message"
                  label={t(
                    'sessions.details.monitoring.meetingRating.helpReport',
                  )}
                />
              )}
            <LabeledSection
              label={t(
                'sessions.details.monitoring.meetingRating.replaceParticipant.question',
              )}
              color="ink"
            >
              <div className={classes.checkContainer}>
                <CheckboxBlockField
                  label={t(
                    'sessions.details.monitoring.meetingRating.replaceParticipant.yes',
                  )}
                  type="radio"
                  name="shouldReplaceParticipant"
                  value={true}
                />
                <CheckboxBlockField
                  label={t(
                    'sessions.details.monitoring.meetingRating.replaceParticipant.no',
                  )}
                  type="radio"
                  name="shouldReplaceParticipant"
                  value={false}
                />
              </div>
              {values.shouldReplaceParticipant === true && (
                <InformationBox variant="question" className={classes.infoBox}>
                  <Trans i18nKey="sessions.details.monitoring.meetingRating.replaceParticipant.information" />
                </InformationBox>
              )}
            </LabeledSection>
            <SubmitButton
              className={classes.submitButton}
              disabled={!values.reportReason}
              text={t('sessions.details.monitoring.meetingRating.submitRating')}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
};
